import { argon2id } from 'hash-wasm';

/**
 * Schlüsselableitung aus dem Portal-Passwort.
 *
 * ACHTUNG — geteilte Krypto über eine Repo-Grenze hinweg.
 *
 * Dieses Modul muss Bit für Bit dieselben Werte erzeugen wie
 * `frontend/src/crypto/kdf.ts` im Dokumentenassistenten. Weicht auch nur ein
 * Zeichen einer `info`-Zeichenkette oder ein Argon2-Parameter ab, entstehen
 * aus demselben Passwort zwei verschiedene Schlüssel — und der Kunde kommt
 * nicht mehr an seinen Akten-Safe. In einem Zero-Knowledge-System ist das
 * nicht reparierbar; auch wir können ihm dann nicht helfen.
 *
 * Abgesichert ist das nicht durch Disziplin, sondern durch feste
 * Testvektoren: `kdf.test.ts` liegt in beiden Repos mit denselben erwarteten
 * Werten. Wer hier etwas ändert, bricht dort einen Test.
 *
 * ---------------------------------------------------------------------------
 * Die Zweige des einen Hauptschlüssels
 * ---------------------------------------------------------------------------
 *
 *   Passwort ──Argon2id(Salz aus E-Mail)──► Hauptschlüssel (nur im RAM)
 *        │
 *        ├─ HKDF "…/portal-anmeldung/v1"   ► Portal-Beweis      → Portal-Server
 *        ├─ HKDF "…/anmeldung/v2"          ► Akten-Beweis       → Akten-Server
 *        └─ HKDF "…/verschluesselung/v2"   ► Akten-Schlüssel    → bleibt im Gerät
 *
 * Die beiden Beweise gehen über die Leitung, der Server speichert davon nur
 * einen scrypt-Hash. Da HKDF einwegig ist, lässt sich aus einem Beweis weder
 * das Passwort noch einer der beiden anderen Zweige gewinnen. Das ist der
 * Punkt, an dem eine einzige Eingabe und Zero-Knowledge zusammengehen.
 *
 * ---------------------------------------------------------------------------
 * Warum es KEINEN vierten Zweig für den Passwort-Safe gibt
 * ---------------------------------------------------------------------------
 *
 * Naheliegend wäre, auch das Masterpasswort für Vaultwarden hier abzuleiten —
 * dann müsste sich der Kunde nur ein Passwort merken. Der Entwurf sah das
 * ursprünglich vor. Es ist eine Datenverlustfalle:
 *
 * Ändert der Kunde sein Portal-Passwort, ändert sich der abgeleitete Wert
 * zwangsläufig mit. Vaultwarden kennt dann weiterhin nur den alten — und den
 * weiß niemand mehr, denn er wurde nie getippt, sondern gerechnet. Der Tresor
 * wäre zu, und zwar endgültig.
 *
 * Automatisch nachziehen lässt sich das nicht: Ein Wechsel des Bitwarden-
 * Masterpassworts verschlüsselt den Benutzerschlüssel neu und verlangt
 * deshalb Bitwardens Krypto im Portal. Das hier nachzubauen hieße, den
 * empfindlichsten Teil fremder Software selbst zu schreiben.
 *
 * Deshalb: Der Passwort-Safe bekommt sein eigenes, vom Kunden gewähltes
 * Masterpasswort. „Ein Passwort für alles" gilt für Portal und Akten-Safe —
 * die gehören uns. Für den Tresor gilt es nicht, und das gehört so gesagt.
 */

export interface KdfParams {
  /** Speicherbedarf in KiB. */
  m: number;
  /** Durchläufe. */
  t: number;
  /** Parallelität. */
  p: number;
  version: number;
}

/**
 * 64 MiB / 3 Durchläufe — identisch zum Akten-Safe. Spürbar, aber auf einem
 * älteren Notebook noch im Bereich einer Sekunde. Die Zielgruppe sitzt nicht
 * zwingend an schneller Hardware.
 */
export const DEFAULT_KDF_PARAMS: KdfParams = { m: 65_536, t: 3, p: 1, version: 19 };

export const SALT_BYTES = 16;

/** Die drei Verwendungszwecke. Nur hier ändern, nie an der Aufrufstelle. */
export const INFO = {
  portalAnmeldung: 'it-karsten-fischer/portal-anmeldung/v1',
  aktenAnmeldung: 'it-karsten-fischer/anmeldung/v2',
  aktenSchluessel: 'it-karsten-fischer/verschluesselung/v2',
} as const;

/**
 * Das Salz stammt aus der E-Mail-Adresse, nicht aus dem Zufall.
 *
 * Es muss schon VOR der Anmeldung feststehen, denn der Anmeldebeweis entsteht
 * ja erst daraus. Ein serverseitig abgelegtes Zufallssalz müsste unangemeldet
 * abrufbar sein — das wäre eine Auskunftsstelle darüber, welche Adressen
 * Kunden sind.
 *
 * Die Zeichenkette ist bewusst die des Akten-Safes: Beide Anwendungen
 * leiten denselben Hauptschlüssel ab, also müssen sie dasselbe Salz benutzen.
 */
export async function deriveSaltFromEmail(email: string): Promise<Uint8Array> {
  const material = new TextEncoder().encode(
    `it-karsten-fischer/dokumentensystem/kdf/v2|${email.trim().toLowerCase()}`,
  );
  const digest = await crypto.subtle.digest('SHA-256', material as BufferSource);
  return new Uint8Array(digest).slice(0, SALT_BYTES);
}

/** Der Hauptschlüssel selbst wird nie benutzt — nur seine Zweige. */
export async function deriveMasterKey(
  email: string,
  password: string,
  params: KdfParams = DEFAULT_KDF_PARAMS,
): Promise<Uint8Array> {
  return argon2id({
    password,
    salt: await deriveSaltFromEmail(email),
    parallelism: params.p,
    iterations: params.t,
    memorySize: params.m,
    hashLength: 32,
    outputType: 'binary',
  });
}

async function hkdf(masterKey: Uint8Array, info: string, bits = 256): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey('raw', masterKey as BufferSource, 'HKDF', false, [
    'deriveBits',
  ]);
  const derived = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      // Salz leer: Die Entropie steckt bereits im Hauptschlüssel aus Argon2id.
      // `info` trennt die Verwendungszwecke.
      salt: new Uint8Array(0) as BufferSource,
      info: new TextEncoder().encode(info) as BufferSource,
    },
    key,
    bits,
  );
  return new Uint8Array(derived);
}

function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

/**
 * Tritt an der Portal-Anmeldung an die Stelle des Passworts. Base64, damit er
 * durch dieselben Wege transportiert werden kann wie bisher das Passwort.
 */
export async function derivePortalAuthSecret(masterKey: Uint8Array): Promise<string> {
  return toBase64(await hkdf(masterKey, INFO.portalAnmeldung));
}

/** Meldet an der Akten-API an. Wird beim Öffnen des Akten-Safes gebraucht. */
export async function deriveAktenAuthSecret(masterKey: Uint8Array): Promise<string> {
  return toBase64(await hkdf(masterKey, INFO.aktenAnmeldung));
}

/**
 * Entpackt den privaten Schlüssel im Akten-Safe. Verlässt das Gerät nie —
 * er wird ausschließlich per postMessage an das eingebettete Fenster gereicht.
 * Rohe Bytes, weil erst der Akten-Safe daraus einen AES-Schlüssel macht.
 */
export async function deriveAktenEncryptionKey(masterKey: Uint8Array): Promise<Uint8Array> {
  return hkdf(masterKey, INFO.aktenSchluessel);
}
